import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Calendar, Plus, Trash2, X, Save,
    MapPin, Clock, Users, Link as LinkIcon,
    Tag, Type, AlignLeft, Info, Search,
    GraduationCap, Mail, Phone, Briefcase
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';

const EventsSection = ({
    events,
    showEventModal,
    setShowEventModal,
    newEvent,
    setNewEvent,
    addEvent,
    deleteEvent,
    participants,
    handleViewParticipants,
    selectedEventForParticipants,
    showParticipantModal,
    setShowParticipantModal,
    handleDeleteRegistration,
    isMobile
}) => {
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <h3 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
                        <Calendar className="text-primary" /> Events Hub
                    </h3>
                    <p className="text-muted-foreground text-xs font-bold uppercase tracking-widest mt-1 opacity-70">
                        Organize workshops, webinars, and college events
                    </p>
                </div>

                <Button
                    onClick={() => setShowEventModal(true)}
                    className="rounded-xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] px-6 shadow-lg shadow-primary/20 gap-2 self-stretch md:self-auto"
                >
                    <Plus size={16} /> Schedule Event
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {events.map((event, i) => (
                    <motion.div
                        key={event.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <Card className="p-6 bg-card/40 backdrop-blur-xl border-border/40 rounded-[2rem] hover:border-primary/20 transition-all group h-full flex flex-col">
                            <div className="flex justify-between items-start mb-4">
                                <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                                    <Calendar size={24} />
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewParticipants(event)}
                                        className="rounded-xl border-border/60 font-black text-[10px] uppercase tracking-widest gap-2 bg-background/50 hover:bg-primary/10 hover:text-primary"
                                    >
                                        <Users size={14} /> Participants
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => deleteEvent(event.id)}
                                        className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"
                                    >
                                        <Trash2 size={18} />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex-1 space-y-4">
                                <div>
                                    <h4 className="text-lg font-black text-foreground tracking-tight mb-1 group-hover:text-primary transition-colors">{event.title}</h4>
                                    <p className="text-muted-foreground text-sm font-medium line-clamp-2 opacity-70">{event.description}</p>
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
                                        <MapPin size={14} className="text-primary/60" /> {event.location || event.venue}
                                    </div>
                                    <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground">
                                        <Clock size={14} className="text-primary/60" /> {event.time}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 pt-6 border-t border-border/40 flex items-center justify-between">
                                <Badge variant="secondary" className="bg-secondary/40 text-muted-foreground border-border/60 font-black text-[10px] uppercase tracking-widest rounded-lg">
                                    {event.date}
                                </Badge>
                                {event.link && (
                                    <a href={event.link} target="_blank" rel="noopener noreferrer" className="text-primary flex items-center gap-1.5 text-xs font-black uppercase tracking-widest hover:underline">
                                        Link <LinkIcon size={12} />
                                    </a>
                                )}
                            </div>
                        </Card>
                    </motion.div>
                ))}

                {events.length === 0 && (
                    <Card className="p-20 text-center bg-card/40 backdrop-blur-xl border-dashed border-2 border-border/60 rounded-[2.5rem] col-span-full">
                        <Calendar size={48} className="mx-auto mb-4 text-muted-foreground/30" />
                        <div className="text-lg font-black text-foreground tracking-tight">No events scheduled</div>
                        <p className="text-muted-foreground text-sm font-bold mt-2 opacity-60">Start by scheduling a new event for the community.</p>
                    </Card>
                )}
            </div>

            {/* Participants Modal */}
            <AnimatePresence>
                {showParticipantModal && selectedEventForParticipants && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 md:p-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowParticipantModal(false)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-lg"
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative w-full max-w-4xl max-h-full overflow-y-auto bg-card border border-border/40 rounded-[2.5rem] shadow-2xl p-6 md:p-10 no-scrollbar"
                        >
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h3 className="text-2xl md:text-3xl font-black text-foreground tracking-tighter">Event Participants</h3>
                                    <p className="text-muted-foreground text-sm font-bold opacity-70">{selectedEventForParticipants.title}</p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowParticipantModal(false)}
                                    className="w-12 h-12 rounded-full border border-border/60 hover:rotate-90 transition-all duration-300"
                                >
                                    <X size={24} />
                                </Button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10">
                                        <div className="text-[10px] font-black uppercase tracking-widest text-primary opacity-60 mb-1">Total Registrations</div>
                                        <div className="text-2xl font-black text-foreground">{participants[selectedEventForParticipants.id]?.length || 0}</div>
                                    </div>
                                </div>

                                <div className="rounded-3xl border border-border/40 overflow-hidden bg-background/50">
                                    <Table>
                                        <TableHeader className="bg-secondary/20">
                                            <TableRow className="hover:bg-transparent border-border/40">
                                                <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-4">Name</TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-4">Contact Info</TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-4">Branch/Year</TableHead>
                                                <TableHead className="text-[10px] font-black uppercase tracking-widest text-muted-foreground py-4 text-right">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {(participants[selectedEventForParticipants.id] || []).length > 0 ? (
                                                (participants[selectedEventForParticipants.id] || []).map((p, idx) => (
                                                    <TableRow key={idx} className="hover:bg-primary/5 transition-colors border-border/40">
                                                        <TableCell className="font-bold text-foreground py-5">{p.fullName}</TableCell>
                                                        <TableCell className="py-5">
                                                            <div className="space-y-1">
                                                                <div className="text-xs font-bold text-muted-foreground flex items-center gap-1.5"><Mail size={12} className="opacity-60" /> {p.email}</div>
                                                                <div className="text-xs font-bold text-muted-foreground flex items-center gap-1.5"><Phone size={12} className="opacity-60" /> {p.whatsapp}</div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="py-5">
                                                            <Badge variant="outline" className="font-bold text-[10px] rounded-lg">
                                                                {p.branch} / {p.year}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="text-right py-5">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => handleDeleteRegistration(p)}
                                                                className="w-9 h-9 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all"
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} className="text-center py-12 text-muted-foreground font-bold opacity-60">
                                                        No participants yet
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showEventModal && (
                    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 md:p-8">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowEventModal(false)}
                            className="absolute inset-0 bg-black/80 backdrop-blur-lg"
                        />
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            className="relative w-full max-w-2xl max-h-full overflow-y-auto bg-card border border-border/40 rounded-[2.5rem] shadow-2xl p-6 md:p-10 no-scrollbar"
                        >
                            <div className="flex items-center justify-between mb-8">
                                <div>
                                    <h3 className="text-2xl md:text-3xl font-black text-foreground tracking-tighter">Schedule Event</h3>
                                    <p className="text-muted-foreground text-sm font-bold opacity-70">Fill in the details for the new event.</p>
                                </div>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setShowEventModal(false)}
                                    className="w-12 h-12 rounded-full border border-border/60 hover:rotate-90 transition-all duration-300"
                                >
                                    <X size={24} />
                                </Button>
                            </div>

                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        <Type size={12} /> Event Title
                                    </label>
                                    <Input
                                        placeholder="e.g. Intro to Cloud Computing"
                                        value={newEvent.title}
                                        onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
                                        className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight"
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                            <Calendar size={12} /> Date
                                        </label>
                                        <Input
                                            type="date"
                                            value={newEvent.date}
                                            onChange={e => setNewEvent({ ...newEvent, date: e.target.value })}
                                            className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                            <Clock size={12} /> Time
                                        </label>
                                        <Input
                                            placeholder="e.g. 5:00 PM - 7:00 PM"
                                            value={newEvent.time}
                                            onChange={e => setNewEvent({ ...newEvent, time: e.target.value })}
                                            className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        <MapPin size={12} /> Location / Platform
                                    </label>
                                    <Input
                                        placeholder="e.g. Google Meet, Lab 402, etc."
                                        value={newEvent.location}
                                        onChange={e => setNewEvent({ ...newEvent, location: e.target.value })}
                                        className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        <LinkIcon size={12} /> Event Link (Optional)
                                    </label>
                                    <Input
                                        placeholder="https://..."
                                        value={newEvent.link}
                                        onChange={e => setNewEvent({ ...newEvent, link: e.target.value })}
                                        className="rounded-2xl py-6 bg-background/50 border-border/60 font-bold tracking-tight"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-2">
                                        <AlignLeft size={12} /> Description
                                    </label>
                                    <Textarea
                                        placeholder="Briefly describe what the event is about..."
                                        value={newEvent.description}
                                        onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                                        className="rounded-2xl min-h-[120px] bg-background/50 border-border/60 font-bold tracking-tight"
                                    />
                                </div>

                                <div className="flex gap-4 pt-6">
                                    <Button
                                        onClick={() => setShowEventModal(false)}
                                        variant="outline"
                                        className="flex-1 py-7 rounded-2xl border-border/60 font-black uppercase tracking-widest text-[10px] hover:bg-secondary/50"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={addEvent}
                                        className="flex-1 py-7 rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-widest text-[10px] shadow-xl shadow-primary/30 flex items-center gap-2"
                                    >
                                        <Save size={18} /> Schedule Event
                                    </Button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default EventsSection;
